/**
 * QA Blocker Validation
 *
 * Validates whether a job has the minimum prerequisites for QA submission.
 * This is a WARNING-ONLY system - it flags issues but does not block submission.
 * This allows QA to proceed for edge cases (owner-performed work, emergency installs)
 * while still capturing data deficiencies for operational visibility.
 *
 * BLOCKED conditions prevent QA submission entirely.
 * WARNING conditions are displayed but allow QA to proceed.
 */

import type { Job } from "./types";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

export interface QABlocker {
  name: "crew" | "schedule" | "materials" | "issues";
  label: string;
  blocked: boolean;
  detail: string;
}

export interface QAReadiness {
  canProceed: boolean;
  blockers: QABlocker[];
  warnings: QABlocker[];
}

/**
 * Check if job has crew assigned (internal crew or subcontractor)
 */
function hasCrew(job: Job): boolean {
  return Boolean(
    String(job.assignedCrew ?? "").trim() ||
    String(job.assignedSubcontractor ?? "").trim() ||
    String(job.crewId ?? "").trim(),
  );
}

/**
 * Check if job has a scheduled install date/time
 */
function hasSchedule(job: Job): boolean {
  return Boolean(
    String(job.scheduledInstallDate ?? "").trim() ||
    String(job.targetInstallDate ?? "").trim() ||
    String(job.scheduledStartTime ?? "").trim(),
  );
}

function hasMaterialReadiness(job: Job): boolean {
  const readiness = String(job.materialReadiness ?? "").trim();
  return ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(readiness);
}

/**
 * Validate QA prerequisites for a job.
 * Returns readiness status with any blockers or warnings.
 *
 * Current policy: HARD BLOCK on missing install-critical prerequisites.
 */
export function validateQAPrerequisites(job: Job): QAReadiness {
  const blockers: QABlocker[] = [];
  const warnings: QABlocker[] = [];

  // Crew assignment is required before QA submission.
  if (!hasCrew(job)) {
    blockers.push({
      name: "crew",
      label: "Crew Assignment",
      blocked: true,
      detail: "No crew or subcontractor assigned to this job",
    });
  }

  // Install schedule is required before QA submission.
  if (!hasSchedule(job)) {
    blockers.push({
      name: "schedule",
      label: "Schedule",
      blocked: true,
      detail: "Job has no scheduled install date",
    });
  }

  // Material readiness must be install-ready.
  // Skip for external-managed imports where ordering is bypassed — their
  // materials readiness is owned by the source system, not Koredor.
  if (!hasMaterialReadiness(job) && !migrationBypasses(job, "ordering")) {
    blockers.push({
      name: "materials",
      label: "Materials",
      blocked: true,
      detail: "Materials are not install-ready",
    });
  }

  return {
    canProceed: blockers.length === 0,
    blockers,
    warnings,
  };
}

/**
 * Check if QA has any warnings (useful for UI indicators)
 */
export function hasQAWarnings(job: Job): boolean {
  const readiness = validateQAPrerequisites(job);
  return readiness.warnings.length > 0;
}

/**
 * Format warning messages for display
 */
export function formatWarningMessages(job: Job): string[] {
  const readiness = validateQAPrerequisites(job);
  return readiness.warnings.map((w) => `${w.label}: ${w.detail}`);
}
